/**
 * StreamingSlidePreview - A standalone, read-only slide template preview component
 * for use in the slides-streaming page.
 *
 * This component is a simplified version of SlideTemplatePreview that:
 * - Does NOT depend on any context providers (useTemplateEditorStore, useTemplateData, useTemplatesStatusStore)
 * - Is read-only (no editing, dragging, or interaction functionality)
 * - Receives all data via props
 */

import { Flex } from "antd";
import styled from "styled-components";
import { forwardRef, useMemo } from "react";
import {
  ITEM_TYPES,
  AvatarShape,
  SHAPE_TYPES,
} from "pages/media/components/storyboardEditor/types";
import type {
  TemplateState,
  MediaItem,
  Shape,
  Avatar,
  ParameteredText,
  CommonTextProperties,
  Header as StoreHeader,
  Size,
  NullableHeightSize,
} from "pages/media/components/storyboardEditor/types";
import type { BrandKit, Slide } from "pages/media/types/";
import { ImageVideoPreview } from "pages/media/components/storyboardEditor/components/common/ImageVideoPreview";
import {
  mapRelativeToPixels,
  resolveParameteredTextPathToSlidePlaceholder,
  getAvatarImagePath,
  resolveColorToBrandkitColorIfNeeded,
  resolveToBrandkitFontIfNeeded,
  resolveToBrandkitVideoIfNeeded,
  getBackgroundStyle,
} from "pages/media/components/storyboardEditor/helpers/itemsHelper";
import { Logo } from "pages/media/components/storyboardEditor/components/Template/Preview/components/Logo";
import { DEFAULT_COLOR } from "pages/media/components/storyboardEditor/constants";
import { useRefreshAssetPath } from "pages/media/components/storyboardEditor/hooks/useRefreshAssetPath";
import { FontPreviewText } from "pages/media/brandkit/components/FontPreview";
import { useOne } from "@refinedev/core";
import type { MediaAsset } from "pages/media/types";
import {
  ASSET_REFRESH_INTERVAL,
  ASSET_STALE_TIME,
} from "pages/media/constants";
import {
  getFontSizeInPixels,
  getRelativeDimentionsToCalculateRadius,
} from "pages/media/components/storyboardEditor/helpers";
import { DEFAULT_RELATIVE_PADDING } from "pages/media/components/storyboardEditor/constants";
import { HoverableCard } from "components/HoverableCard/";
import { useBrandKit } from "hooks/useBrandKit/";

export type StreamingSlidePreviewProps = {
  template: TemplateState;
  onCardClick: () => void;
  border: {
    borderWidth: number;
    borderColor?: string;
    borderRadius: number;
  };
  templateSize: { width: number; height: number };
  slide: Slide;
  brandKitData: ReturnType<typeof useBrandKit>;
  /** Skip rendering of elements that depend on slide placeholder data
   *  (parametered texts, main image). Useful for custom project carousel
   *  where no real slide data exists. */
  skipSlideContent?: boolean;
  /** When provided, resolves the background using type-specific assets
   *  (intro/closing/transition videos) like the storyboard editor does. */
  chapterType?: string;
  /** Text to display as an overlay, like the storyboard editor's previewText.
   *  Rendered using template.textStyle (chapters) or template.titleStyle (transitions). */
  previewText?: string;
  /** Strip outer vertical margin so the rendered card matches `templateSize` exactly.
   *  Used by the autopilot streaming visuals where the wrapper is sized to the preview. */
  compact?: boolean;
};

const StyledPreview = styled.div<{ width: number; height: number }>`
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  position: relative;
  overflow: hidden;
`;

export const StreamingSlidePreview = forwardRef<
  HTMLDivElement,
  StreamingSlidePreviewProps
>(
  (
    {
      template,
      onCardClick,
      border: { borderWidth, borderColor, borderRadius },
      templateSize,
      slide,
      brandKitData,
      skipSlideContent,
      chapterType,
      previewText,
      compact = false,
    },
    ref
  ) => {
    const { width, height } = templateSize;

    const backgroundMediaItem = useMemo(() => {
      // When chapterType is provided, resolve type-specific backgrounds
      // (intro/closing/transition videos) like the storyboard editor does.
      if (chapterType === "Introduction" && template.intro.enable) {
        return {
          type: ITEM_TYPES.Video,
          assetId: resolveToBrandkitVideoIfNeeded(
            template.intro.assetId,
            brandKitData.intro,
            brandKitData.closing
          ),
          assetPath: template.intro.assetPath,
        };
      }
      if (chapterType === "Closing" && template.closing.enable) {
        return {
          type: ITEM_TYPES.Video,
          assetId: resolveToBrandkitVideoIfNeeded(
            template.closing.assetId,
            brandKitData.intro,
            brandKitData.closing
          ),
          assetPath: template.closing.assetPath,
        };
      }
      if (
        chapterType === "Transition" &&
        template.transition.enable &&
        template.transition.view !== "Color"
      ) {
        return {
          type:
            template.transition.view === "Video"
              ? ITEM_TYPES.Video
              : ITEM_TYPES.Image,
          assetId: template.transition.assetId,
          assetPath: template.transition.assetPath,
        };
      }

      // Default: use template.background (for Chapter type or flash slides).
      if (template.background.bodyView === "Video") {
        return {
          type: ITEM_TYPES.Video,
          assetId: resolveToBrandkitVideoIfNeeded(
            template.background.bodyVideoAsset,
            brandKitData.intro,
            brandKitData.closing
          ),
          assetPath: template.background.bodyVideo,
        };
      } else if (template.background.bodyView === "Picture") {
        return {
          type: ITEM_TYPES.Image,
          assetId: template.background.bodyImageAsset,
          assetPath: template.background.bodyImage,
        };
      }
      return undefined;
    }, [template, chapterType, brandKitData.intro, brandKitData.closing]);

    const mainImage = useMemo(
      () =>
        !skipSlideContent &&
        template.mainImage &&
        "image_element" in slide.placeholders &&
        slide.placeholders.image_element.asset_id && {
          ...template.mainImage,
          assetId: slide.placeholders.image_element.asset_id,
        },
      [slide.placeholders, template.mainImage, skipSlideContent]
    );

    const backgroundStyle = useMemo(() => {
      if (!template.background.bodyColor) return {};
      return getBackgroundStyle(
        template.background.bodyColor,
        brandKitData.brandKitPalette
      );
    }, [template.background.bodyColor, brandKitData.brandKitPalette]);

    return (
      <Flex
        vertical
        gap={compact ? 0 : 10}
        style={{ position: "relative", marginBlock: compact ? 0 : 24 }}
      >
        <HoverableCard
          ref={ref}
          data-interactive
          data-testid="hoverable-card"
          hoverable
          styles={{
            body: {
              padding: 0,
              overflow: "hidden",
              borderRadius: borderRadius - borderWidth,
              width: "fit-content",
            },
          }}
          style={{
            borderRadius,
            borderWidth,
            borderColor,
            width: "fit-content",
            margin: "auto",
          }}
          onClick={onCardClick}
        >
          <StyledPreview width={width} height={height}>
            {backgroundMediaItem?.assetId && (
              <ImageVideoPreview
                type={backgroundMediaItem.type}
                assetId={backgroundMediaItem.assetId}
                path={backgroundMediaItem.assetPath}
              />
            )}
            {template.background.bodyColor && (
              <div
                style={{
                  width,
                  height,
                  ...backgroundStyle,
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />
            )}
            <StaticImages
              images={[
                ...(mainImage &&
                slide.placeholders.image_element.media_type === "image"
                  ? [mainImage]
                  : []),
                ...template.images,
              ]}
              previewWidth={width}
              previewHeight={height}
            />
            <StaticVideos
              videos={[
                ...(mainImage &&
                slide.placeholders.image_element.media_type === "video"
                  ? [mainImage]
                  : []),
                ...template.videos,
              ]}
              previewWidth={width}
              previewHeight={height}
            />
            <StaticParameteredTexts
              texts={
                [
                  ...(template.parameteredTexts ?? []),
                  ...(template.figure?.number ? [template.figure.number] : []),
                  ...(template.figure?.units &&
                  "percent" in template.figure.units
                    ? [template.figure.units.percent]
                    : []),
                  ...(template.figure?.units &&
                  "default" in template.figure.units
                    ? [template.figure.units.default]
                    : []),
                ] as ParameteredText[]
              }
              slide={slide}
              previewWidth={width}
              previewHeight={height}
              brandKitData={brandKitData}
            />
            <StaticShapes
              shapes={[
                ...template.shapes,
                ...(template.slideLayoutShapes ?? []),
              ]}
              previewWidth={width}
              previewHeight={height}
              brandKitData={brandKitData}
            />
            {template.logo.showLogo &&
              (!chapterType || chapterType === "Chapter") && (
                <>
                  <StaticHeader
                    headerItem={template.header}
                    previewWidth={width}
                    previewHeight={height}
                    brandKitData={brandKitData}
                  />
                  <div
                    style={{
                      position: "absolute",
                      zIndex: template.logo.layerIndex,
                      top: template.logo.position.y * height,
                      left: template.logo.position.x * width,
                      width: template.logo.size.width * width,
                      height: template.logo.size.height * height,
                      transformOrigin: "center",
                      transform: template.logo.rotation
                        ? `rotate(${template.logo.rotation}deg)`
                        : undefined,
                    }}
                  >
                    <Logo
                      logo={template.logo}
                      previewWidth={width}
                      previewHeight={height}
                    />
                  </div>
                </>
              )}
            {previewText &&
              chapterType === "Chapter" &&
              template.textStyle.showSubtitles && (
                <div
                  style={{
                    position: "absolute",
                    overflow: "hidden",
                    top: template.textStyle.position.y * height,
                    left: template.textStyle.position.x * width,
                    zIndex: template.textStyle.layerIndex,
                    transformOrigin: "center",
                    transform: template.textStyle.rotation
                      ? `rotate(${template.textStyle.rotation}deg)`
                      : undefined,
                  }}
                >
                  <StaticText
                    textItem={{
                      ...template.textStyle,
                      content: previewText,
                    }}
                    previewWidth={width}
                    previewHeight={height}
                    brandKitData={brandKitData}
                  />
                </div>
              )}
            {previewText &&
              chapterType === "Transition" &&
              template.titleStyle.showTitle && (
                <div
                  style={{
                    position: "absolute",
                    overflow: "hidden",
                    top: template.titleStyle.position.y * height,
                    left: template.titleStyle.position.x * width,
                    zIndex: template.titleStyle.layerIndex,
                    transformOrigin: "center",
                    transform: template.titleStyle.rotation
                      ? `rotate(${template.titleStyle.rotation}deg)`
                      : undefined,
                  }}
                >
                  <StaticText
                    textItem={{
                      ...template.titleStyle,
                      content: previewText,
                    }}
                    previewWidth={width}
                    previewHeight={height}
                    brandKitData={brandKitData}
                  />
                </div>
              )}
            {template.presenter.presenter && (
              <StaticPresenter
                presenter={template.presenter}
                previewWidth={width}
                previewHeight={height}
                brandKitData={brandKitData}
              />
            )}
          </StyledPreview>
        </HoverableCard>
      </Flex>
    );
  }
);

StreamingSlidePreview.displayName = "StreamingSlidePreview";

const StaticImages = ({
  images,
  previewWidth,
  previewHeight,
}: {
  images?: MediaItem[];
  previewWidth: number;
  previewHeight: number;
}) => {
  if (!images || images.length === 0) return null;

  return (
    <>
      {images.map((image) => {
        const strokeWidth = Math.round(
          (image.stroke?.width ?? 0) * previewWidth
        );
        const doubleStrokeWidth = strokeWidth * 2;

        return (
          <div
            key={image.id}
            style={{
              position: "absolute",
              top: Math.round(image.position.y * previewHeight) - strokeWidth,
              left: Math.round(image.position.x * previewWidth) - strokeWidth,
              width:
                Math.round(image.size.width * previewWidth) + doubleStrokeWidth,
              height:
                Math.round(image.size.height * previewHeight) +
                doubleStrokeWidth,
              zIndex: image.layerIndex,
              transformOrigin: "center",
              transform: image.rotation
                ? `rotate(${image.rotation}deg)`
                : undefined,
            }}
          >
            <ImageVideoPreview
              type={ITEM_TYPES.Image}
              assetId={image.assetId}
              path={image.path}
              style={
                image.stroke?.width
                  ? {
                      border: `solid ${strokeWidth}px ${image.stroke.color ?? "transparent"}`,
                      borderRadius: image.stroke.radius
                        ? `${image.stroke.radius * previewWidth}px`
                        : undefined,
                    }
                  : undefined
              }
            />
          </div>
        );
      })}
    </>
  );
};

const StaticVideos = ({
  videos,
  previewWidth,
  previewHeight,
}: {
  videos?: MediaItem[];
  previewWidth: number;
  previewHeight: number;
}) => {
  if (!videos || videos.length === 0) return null;

  return (
    <>
      {videos.map((video) => {
        const strokeWidth = Math.round(
          (video.stroke?.width ?? 0) * previewWidth
        );
        const doubleStrokeWidth = strokeWidth * 2;

        return (
          <div
            key={video.id}
            style={{
              position: "absolute",
              top: Math.round(video.position.y * previewHeight) - strokeWidth,
              left: Math.round(video.position.x * previewWidth) - strokeWidth,
              width:
                Math.round(video.size.width * previewWidth) + doubleStrokeWidth,
              height:
                Math.round(video.size.height * previewHeight) +
                doubleStrokeWidth,
              zIndex: video.layerIndex,
              transformOrigin: "center",
              transform: video.rotation
                ? `rotate(${video.rotation}deg)`
                : undefined,
            }}
          >
            <ImageVideoPreview
              type={ITEM_TYPES.Video}
              assetId={video.assetId}
              path={video.path}
              style={
                video.stroke?.width
                  ? {
                      border: `solid ${strokeWidth}px ${video.stroke.color ?? "transparent"}`,
                    }
                  : undefined
              }
            />
          </div>
        );
      })}
    </>
  );
};

const StaticParameteredTexts = ({
  texts,
  slide,
  previewWidth,
  previewHeight,
  brandKitData,
}: {
  texts: ParameteredText[] | undefined;
  slide: Slide;
  previewWidth: number;
  previewHeight: number;
  brandKitData: ReturnType<typeof useBrandKit>;
}) => {
  return texts?.map((text) => {
    const textContent = resolveParameteredTextPathToSlidePlaceholder(
      text.path,
      slide,
      text.index
    );
    if (!textContent) {
      return null;
    }

    return (
      <div
        key={text.id}
        style={{
          position: "absolute",
          top: mapRelativeToPixels(text.position.y, previewHeight),
          left: mapRelativeToPixels(text.position.x, previewWidth),
          zIndex: text.layerIndex,
          transformOrigin: "center",
          transform: text.rotation ? `rotate(${text.rotation}deg)` : undefined,
        }}
      >
        <StaticText
          textItem={{ ...text, content: textContent }}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          brandKitData={brandKitData}
        />
      </div>
    );
  });
};

const VERTICAL_ALIGN_MAP = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
};

const StaticText = ({
  textItem,
  previewWidth,
  previewHeight,
  brandKitData,
}: {
  textItem: Omit<CommonTextProperties, "size"> & {
    content: string;
    animated?: boolean;
    size: Size | NullableHeightSize;
  } & { size: Size | NullableHeightSize };
  previewWidth: number;
  previewHeight: number;
  brandKitData: ReturnType<typeof useBrandKit>;
}) => {
  const { brandKitPalette, titleFont, subtitlesFont, textFont } = brandKitData;

  const fontSizeInPixels = getFontSizeInPixels(textItem.fontSize, previewWidth);

  const fontAssetId = resolveToBrandkitFontIfNeeded(
    textItem.fontAssetId,
    titleFont,
    subtitlesFont,
    textFont
  );

  const { data: fontAsset, isFetched } = useOne<MediaAsset>({
    resource: `media/media/assets`,
    id: fontAssetId,
    queryOptions: {
      // fetch new asset even if we have fontPath
      // we had weird stale data when adding fontPath condition in enabled
      enabled: !!fontAssetId,
      staleTime: ASSET_STALE_TIME,
      refetchInterval: ASSET_REFRESH_INTERVAL,
    },
  });

  const fontPath = fontAsset?.data.path;

  if (isFetched && !fontPath) {
    console.error("Font path not resolved for text item", {
      textItem,
      fontAssetId,
    });
    return;
  }

  const shouldWrapText = textItem.wrapText ?? true;
  const hasBackground = !!textItem.backgroundColor;
  const padding = hasBackground
    ? `${fontSizeInPixels * DEFAULT_RELATIVE_PADDING}px`
    : undefined;

  const containerHeight = mapRelativeToPixels(
    textItem.size.height ?? 0,
    previewHeight
  );
  const hasVerticalAlignment =
    containerHeight > 0 &&
    textItem.verticalAlign &&
    textItem.verticalAlign !== "top";

  const verticalAlignStyles: React.CSSProperties = hasVerticalAlignment
    ? {
        height: containerHeight,
        minHeight: containerHeight,
        display: "flex",
        flexDirection: "column",
        justifyContent: VERTICAL_ALIGN_MAP[textItem.verticalAlign ?? "top"],
      }
    : {};

  return fontPath ? (
    <FontPreviewText
      key={textItem.fontAssetId}
      style={{
        width: mapRelativeToPixels(textItem.size.width, previewWidth),
        height: containerHeight > 0 ? containerHeight : "fit-content",
        fontSize: fontSizeInPixels,
        fontWeight: textItem.bold ? "bold" : "normal",
        fontStyle: textItem.italic ? "italic" : "normal",
        textDecoration: textItem.underline ? "underline" : "none",
        backgroundColor: resolveColorToBrandkitColorIfNeeded(
          textItem.backgroundColor,
          brandKitPalette
        ),
        color: resolveColorToBrandkitColorIfNeeded(
          textItem.color,
          brandKitPalette
        ),
        WebkitTextStroke: textItem.strokeColor
          ? `0.1em ${resolveColorToBrandkitColorIfNeeded(textItem.strokeColor, brandKitPalette)}`
          : "unset",
        whiteSpace: shouldWrapText ? "normal" : "nowrap",
        //https://stackoverflow.com/questions/69253420/text-stroke-webkit-text-stroke-css-problem
        paintOrder: "stroke fill",
        lineHeight: 1 + textItem.lineHeight,
        padding,
        ...verticalAlignStyles,
        textAlign: textItem.textAlign,
        zIndex: textItem.layerIndex,
      }}
      font={fontPath}
      animated={"animated" in textItem ? textItem.animated : false}
      text={textItem.content}
    />
  ) : null;
};

const StaticShapes = ({
  shapes,
  previewWidth,
  previewHeight,
  brandKitData,
}: {
  shapes: Shape[];
  previewWidth: number;
  previewHeight: number;
  brandKitData: ReturnType<typeof useBrandKit>;
}) => {
  const { brandKitPalette } = brandKitData;

  return shapes.map((shape) => {
    const strokeWidth = Math.round((shape.stroke?.width ?? 0) * previewWidth);
    const doubleStrokeWidth = strokeWidth * 2;
    const borderRadius = shape.stroke?.radius
      ? shape.stroke.radius *
        getRelativeDimentionsToCalculateRadius(previewWidth, previewHeight)
      : 0;
    const style = getShapeStyle(shape, brandKitPalette);

    return (
      <div
        key={shape.id}
        style={{
          position: "absolute",
          top: shape.position.y * previewHeight - strokeWidth,
          left: shape.position.x * previewWidth - strokeWidth,
          width: shape.size.width * previewWidth + doubleStrokeWidth,
          height: shape.size.height * previewHeight + doubleStrokeWidth,
          zIndex: shape.layerIndex,
          transformOrigin: "center",
          transform: shape.rotation
            ? `rotate(${shape.rotation}deg)`
            : undefined,
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            border: shape.stroke?.width
              ? `solid ${shape.stroke.width * previewWidth}px ${resolveColorToBrandkitColorIfNeeded(shape.stroke.color ?? undefined, brandKitPalette)}`
              : "none",
            borderRadius,
            ...style,
          }}
        />
      </div>
    );
  });
};

const getShapeStyle = (
  shape: Shape,
  brandkitPalette: BrandKit["palette"] | undefined
): React.CSSProperties => {
  const { shapeType, background } = shape;

  switch (shapeType) {
    case SHAPE_TYPES.RECTANGLE:
    case SHAPE_TYPES.LINE:
      return getBackgroundStyle(background, brandkitPalette);
    case SHAPE_TYPES.CIRCLE:
      return {
        ...getBackgroundStyle(background, brandkitPalette),
        borderRadius: "50%",
      };
    default:
      return {};
  }
};

const StaticPresenter = ({
  presenter,
  previewWidth,
  previewHeight,
  brandKitData,
}: {
  presenter: Avatar;
  previewWidth: number;
  previewHeight: number;
  brandKitData: ReturnType<typeof useBrandKit>;
}) => {
  const { brandKitPalette } = brandKitData;
  const avatarHasShape = presenter.avatarShape !== AvatarShape.Regular;

  const avatarImagePath = useMemo(() => {
    return getAvatarImagePath(presenter);
  }, [presenter]);

  const avatarWidth = useMemo(() => {
    if (presenter.avatarShape === AvatarShape.Regular) {
      return mapRelativeToPixels(presenter.size.width, previewWidth);
    }
    return mapRelativeToPixels(presenter.size.height, previewHeight);
  }, [
    presenter.avatarShape,
    presenter.size.height,
    presenter.size.width,
    previewHeight,
    previewWidth,
  ]);

  const borderRadius = useMemo(() => {
    if (presenter.avatarShape === AvatarShape.Circle) {
      return "50%";
    }
    if (presenter.stroke?.radius) {
      return `${presenter.stroke.radius * previewWidth}px`;
    }
    return "unset";
  }, [presenter.avatarShape, presenter.stroke, previewWidth]);

  if (!avatarImagePath) return null;

  return (
    <Flex
      align="center"
      justify="center"
      style={{
        position: "absolute",
        top: mapRelativeToPixels(presenter.position.y, previewHeight),
        left: mapRelativeToPixels(presenter.position.x, previewWidth),
        borderRadius: borderRadius,
        ...(avatarHasShape && presenter.background
          ? getBackgroundStyle(presenter.background, brandKitPalette)
          : { backgroundColor: "transparent" }),
        overflow: "hidden",
        border: presenter.stroke?.width
          ? `solid ${presenter.stroke.width * previewWidth}px ${presenter.stroke.color}`
          : "none",
        width: avatarWidth,
        height: mapRelativeToPixels(presenter.size.height, previewHeight),
        zIndex: presenter.layerIndex,
      }}
    >
      <img
        src={avatarImagePath}
        alt="Presenter"
        draggable="false"
        style={{
          width: "auto",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center top",
        }}
      />
    </Flex>
  );
};

const StaticHeader = ({
  headerItem,
  previewWidth,
  previewHeight,
  brandKitData,
}: {
  headerItem: StoreHeader;
  previewWidth: number;
  previewHeight: number;
  brandKitData: ReturnType<typeof useBrandKit>;
}) => {
  let headerImageAssetId: string | undefined;
  let headerImagePath: string | undefined;

  if (headerItem.view === "Picture") {
    headerImageAssetId = headerItem.banner?.assetId;
    headerImagePath = headerItem.banner?.path;
  }

  const { currentPath: headerAssetPath } = useRefreshAssetPath({
    assetId: headerImageAssetId,
    path: headerImagePath,
  });
  const backgroundUrl = headerAssetPath ? `url(${headerAssetPath})` : "unset";

  const resolvedBackground = getBackgroundStyle(
    headerItem.background ?? DEFAULT_COLOR,
    brandKitData.brandKitPalette
  );

  return (
    <div
      style={{
        height: mapRelativeToPixels(headerItem.size.height, previewHeight),
        width: mapRelativeToPixels(headerItem.size.width, previewWidth),
        ...resolvedBackground,
        ...(headerItem.view === "Picture" && {
          backgroundImage: backgroundUrl,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }),
        position: "absolute",
        top: mapRelativeToPixels(headerItem.position.y, previewHeight),
        left: mapRelativeToPixels(headerItem.position.x, previewWidth),
        zIndex: headerItem.layerIndex,
      }}
    />
  );
};
